import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { uploadOptimizedImage } from "@/lib/image-upload";
import {
  getOnsitePageData,
  upsertOnsiteContent,
  upsertOnsiteAmenity,
  deleteOnsiteAmenity,
  reorderOnsiteAmenities,
  ONSITE_GALLERY_IMAGE_TABLE,
  type OnsiteContent,
  type OnsiteAmenity,
  type OnsiteGalleryImage,
} from "@/lib/onsite.functions";
import {
  getNearbyPageData,
  upsertNearbyContent,
  upsertNearbyAttraction,
  deleteNearbyAttraction,
  reorderNearbyAttractions,
  NEARBY_GALLERY_IMAGE_TABLE,
  type NearbyContent,
  type NearbyAttraction,
  type NearbyGalleryImage,
} from "@/lib/nearby.functions";
import { ImageExperiencesCell } from "@/components/admin/overrides/ImageExperiencesCell";
import { FieldOverridesButton } from "@/components/admin/overrides/FieldOverridesButton";
import {
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  Trash2,
  Upload,
  Loader2,
  Plus,
  Pencil,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/things-to-do")({
  head: () => ({
    meta: [{ title: "Things To Do CMS" }, { name: "robots", content: "noindex" }],
  }),
  component: AdminThingsToDoPage,
});

function AdminThingsToDoPage() {
  return (
    <div
      className="fixed inset-0 overflow-y-auto bg-background text-foreground"
      style={{ touchAction: "auto", overscrollBehavior: "auto" }}
    >
      <Toaster richColors closeButton position="top-right" />
      <div className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        <div>
          <Link
            to="/admin"
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2"
          >
            <ArrowLeft className="w-3 h-3" /> Admin
          </Link>
          <h1 className="text-2xl font-serif">Things To Do CMS</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Edit both the Onsite (amenities) and Nearby (attractions) sections of the Things To Do page.
          </p>
        </div>

        <Tabs defaultValue="onsite">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="onsite">Onsite</TabsTrigger>
            <TabsTrigger value="nearby">Nearby</TabsTrigger>
          </TabsList>
          <TabsContent value="onsite" className="mt-6">
            <OnsiteAdminPanel />
          </TabsContent>
          <TabsContent value="nearby" className="mt-6">
            <NearbyAdminPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

/* =============================================================
 * ONSITE
 * ============================================================= */

const ONSITE_PAGE_KEY = "onsite";

async function uploadOnsite(folder: string, file: File): Promise<string> {
  const { url } = await uploadOptimizedImage({
    file,
    bucket: "slide-media",
    folder: `onsite/${folder}`,
  });
  return url;
}
const uploadOnsiteHero = (f: File) => uploadOnsite("hero", f);

function OnsiteAdminPanel() {
  const fetch = useServerFn(getOnsitePageData);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "onsite"],
    queryFn: () => fetch(),
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "onsite"] });
    qc.invalidateQueries({ queryKey: ["onsite", "page"] });
  };
  if (isLoading || !data) {
    return <div className="text-sm text-muted-foreground py-6">Loading…</div>;
  }
  return (
    <div className="space-y-10">
      <OnsiteContentPanel content={data.content} onSaved={invalidate} />
      <AmenitiesPanel amenities={data.amenities} onChanged={invalidate} />
    </div>
  );
}

function OnsiteContentPanel({
  content,
  onSaved,
}: {
  content: OnsiteContent | null;
  onSaved: () => void;
}) {
  const upsert = useServerFn(upsertOnsiteContent);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [heroUrl, setHeroUrl] = useState(content?.hero_image_url ?? "");
  const [kicker, setKicker] = useState(content?.hero_kicker ?? "");
  const [title, setTitle] = useState(content?.hero_title ?? "");
  const [subtitle, setSubtitle] = useState(content?.hero_subtitle ?? "");
  const [intro, setIntro] = useState(content?.intro_body ?? "");
  const [tabLabel, setTabLabel] = useState(content?.tab_label ?? "");

  const save = useMutation({
    mutationFn: () =>
      upsert({
        data: {
          id: content?.id,
          hero_image_url: heroUrl || null,
          hero_kicker: kicker || null,
          hero_title: title || null,
          hero_subtitle: subtitle || null,
          intro_body: intro || null,
          tab_label: tabLabel || null,
        },
      }),
    onSuccess: () => { toast.success("Saved"); onSaved(); },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const handleUpload = async (f: File) => {
    setUploading(true);
    try {
      const url = await uploadOnsite("hero", f);
      setHeroUrl(url);
      await upsert({
        data: {
          id: content?.id,
          hero_image_url: url,
          hero_kicker: kicker || null,
          hero_title: title || null,
          hero_subtitle: subtitle || null,
          intro_body: intro || null,
          tab_label: tabLabel || null,
        },
      });
      toast.success("Hero image saved");
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5 space-y-5">
      <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Hero & intro</h2>
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Hero image</Label>
          <FieldOverridesButton pageKey={ONSITE_PAGE_KEY} fieldKey="hero_image" label="Hero image" type="image" onUploadImage={uploadOnsiteHero} />
        </div>
        <div className="flex items-start gap-3">
          {heroUrl ? <img src={heroUrl} alt="" className="w-32 h-32 object-cover rounded" /> : <div className="w-32 h-32 rounded bg-muted" />}
          <div className="flex-1 space-y-2">
            <Input value={heroUrl} onChange={(e) => setHeroUrl(e.target.value)} placeholder="https://… or upload" />
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); }} />
            <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Upload
            </Button>
          </div>
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2"><Label>Kicker</Label><FieldOverridesButton pageKey={ONSITE_PAGE_KEY} fieldKey="hero_kicker" label="Kicker" type="text" /></div>
        <Input value={kicker} onChange={(e) => setKicker(e.target.value)} placeholder="On the Land" />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2"><Label>Title</Label><FieldOverridesButton pageKey={ONSITE_PAGE_KEY} fieldKey="hero_title" label="Title" type="text" /></div>
        <Textarea value={title} onChange={(e) => setTitle(e.target.value)} rows={2} placeholder={"Onsite\n(press Enter to break line)"} />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2"><Label>Tab button label</Label><FieldOverridesButton pageKey={ONSITE_PAGE_KEY} fieldKey="tab_label" label="Tab button label" type="text" /></div>
        <Input value={tabLabel} onChange={(e) => setTabLabel(e.target.value)} placeholder="Defaults to the hero title" />
        <p className="text-xs text-muted-foreground mt-1">Text shown on the tab switcher button. Leave blank to use the hero title.</p>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2"><Label>Subtitle</Label><FieldOverridesButton pageKey={ONSITE_PAGE_KEY} fieldKey="hero_subtitle" label="Subtitle" type="longtext" /></div>
        <Textarea value={subtitle} onChange={(e) => setSubtitle(e.target.value)} rows={2} />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2"><Label>Intro paragraph</Label><FieldOverridesButton pageKey={ONSITE_PAGE_KEY} fieldKey="intro_body" label="Intro paragraph" type="longtext" /></div>
        <Textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={4} placeholder="Line breaks are preserved." />
      </div>
      <Button onClick={() => save.mutate()} disabled={save.isPending}>
        {save.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save
      </Button>
    </div>
  );
}

function AmenitiesPanel({ amenities, onChanged }: { amenities: OnsiteAmenity[]; onChanged: () => void }) {
  const reorder = useServerFn(reorderOnsiteAmenities);
  const del = useServerFn(deleteOnsiteAmenity);
  const [editing, setEditing] = useState<OnsiteAmenity | null>(null);
  const [creating, setCreating] = useState(false);

  const move = async (idx: number, dir: -1 | 1) => {
    const next = [...amenities];
    const t = idx + dir;
    if (t < 0 || t >= next.length) return;
    [next[idx], next[t]] = [next[t], next[idx]];
    await reorder({ data: { ordered_ids: next.map((a) => a.id) } });
    onChanged();
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Amenities</h2>
        <Button size="sm" variant="outline" onClick={() => setCreating(true)}><Plus className="w-4 h-4 mr-1" /> Add amenity</Button>
      </div>
      {amenities.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">No amenities yet.</p>
      ) : (
        <div className="space-y-2">
          {amenities.map((am, i) => (
            <div key={am.id} className="flex items-center gap-3 rounded-md border border-border bg-background p-2">
              {am.cover_image_url ? <img src={am.cover_image_url} alt="" className="w-16 h-16 object-cover rounded" /> : <div className="w-16 h-16 rounded bg-muted" />}
              <div className="flex-1 min-w-0">
                <div className="font-serif truncate">{am.label}</div>
                <div className="text-xs text-muted-foreground truncate">{am.slug} · {am.gallery.length} image{am.gallery.length === 1 ? "" : "s"}</div>
              </div>
              <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp className="w-3.5 h-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === amenities.length - 1} onClick={() => move(i, 1)}><ArrowDown className="w-3.5 h-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(am)}><Pencil className="w-3.5 h-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={async () => { if (!confirm(`Delete "${am.label}"?`)) return; await del({ data: { id: am.id } }); onChanged(); }}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
        </div>
      )}
      {editing && <AmenityEditor amenity={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />}
      {creating && <AmenityEditor amenity={null} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); onChanged(); }} />}
    </div>
  );
}

function AmenityEditor({ amenity, onClose, onSaved }: { amenity: OnsiteAmenity | null; onClose: () => void; onSaved: () => void }) {
  const upsert = useServerFn(upsertOnsiteAmenity);
  const [slug, setSlug] = useState(amenity?.slug ?? "");
  const [label, setLabel] = useState(amenity?.label ?? "");
  const [cover, setCover] = useState(amenity?.cover_image_url ?? "");
  const [description, setDescription] = useState(amenity?.description ?? "");
  const [gallery, setGallery] = useState<OnsiteGalleryImage[]>(amenity?.gallery ?? []);
  const [coverUploading, setCoverUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const coverRef = useRef<HTMLInputElement | null>(null);
  const galleryRef = useRef<HTMLInputElement | null>(null);
  const slugBase = amenity?.slug || slug || "new";

  const handleCover = async (f: File) => {
    setCoverUploading(true);
    try { const url = await uploadOnsite(`${slugBase}/cover`, f); setCover(url); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Upload failed"); }
    finally { setCoverUploading(false); if (coverRef.current) coverRef.current.value = ""; }
  };
  const handleGallery = async (files: FileList) => {
    setGalleryUploading(true);
    try {
      const uploaded: OnsiteGalleryImage[] = [];
      for (const f of Array.from(files)) {
        const url = await uploadOnsite(`${slugBase}/gallery`, f);
        uploaded.push({ id: crypto.randomUUID(), url });
      }
      setGallery((g) => [...g, ...uploaded]);
    } catch (e) { toast.error(e instanceof Error ? e.message : "Upload failed"); }
    finally { setGalleryUploading(false); if (galleryRef.current) galleryRef.current.value = ""; }
  };
  const moveGallery = (idx: number, dir: -1 | 1) => {
    const t = idx + dir; if (t < 0 || t >= gallery.length) return;
    const next = [...gallery]; [next[idx], next[t]] = [next[t], next[idx]]; setGallery(next);
  };
  const removeGallery = (idx: number) => setGallery((g) => g.filter((_, i) => i !== idx));
  const autoSlug = (val: string) => val.toLowerCase().replace(/['']/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);

  const save = async () => {
    if (!label.trim()) { toast.error("Label is required"); return; }
    const finalSlug = slug.trim() || autoSlug(label);
    if (!/^[a-z0-9-]+$/.test(finalSlug)) { toast.error("Slug must be lowercase letters, numbers, and dashes"); return; }
    setSaving(true);
    try {
      await upsert({ data: { id: amenity?.id, slug: finalSlug, label: label.trim(), cover_image_url: cover || null, description: description || null, gallery } });
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Save failed"); }
    finally { setSaving(false); }
  };

  const isNew = !amenity;
  const fieldPrefix = `amenity:${amenity?.slug ?? ""}`;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90svh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isNew ? "New amenity" : `Edit: ${amenity!.label}`}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Label</Label>
              {!isNew && <FieldOverridesButton pageKey={ONSITE_PAGE_KEY} fieldKey={`${fieldPrefix}:label`} label={`${amenity!.label} — Label`} type="text" />}
            </div>
            <Input value={label} onChange={(e) => { setLabel(e.target.value); if (isNew && !slug) setSlug(autoSlug(e.target.value)); }} placeholder="Stargazing" />
          </div>
          <div>
            <Label className="mb-2 block">Slug</Label>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="stargazing" />
            <p className="text-xs text-muted-foreground mt-1">Used internally. Lowercase letters, numbers, and dashes.</p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Cover photo</Label>
              {!isNew && <FieldOverridesButton pageKey={ONSITE_PAGE_KEY} fieldKey={`${fieldPrefix}:cover`} label={`${amenity!.label} — Cover`} type="image" onUploadImage={(f) => uploadOnsite(`${amenity!.slug}/cover`, f)} />}
            </div>
            <div className="flex items-start gap-3">
              {cover ? <img src={cover} alt="" className="w-24 h-24 object-cover rounded" /> : <div className="w-24 h-24 rounded bg-muted" />}
              <div className="flex-1 space-y-2">
                <Input value={cover} onChange={(e) => setCover(e.target.value)} placeholder="https://… or upload" />
                <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCover(f); }} />
                <Button type="button" size="sm" variant="outline" onClick={() => coverRef.current?.click()} disabled={coverUploading}>
                  {coverUploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Upload
                </Button>
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Description</Label>
              {!isNew && <FieldOverridesButton pageKey={ONSITE_PAGE_KEY} fieldKey={`${fieldPrefix}:description`} label={`${amenity!.label} — Description`} type="longtext" />}
            </div>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder="Shown in the popup." />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Gallery images</Label>
              <div>
                <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { if (e.target.files?.length) handleGallery(e.target.files); }} />
                <Button size="sm" variant="outline" onClick={() => galleryRef.current?.click()} disabled={galleryUploading}>
                  {galleryUploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Add images
                </Button>
              </div>
            </div>
            {gallery.length === 0 ? <p className="text-sm text-muted-foreground italic">No gallery images yet.</p> : (
              <div className="grid grid-cols-3 gap-2">
                {gallery.map((img, i) => (
                  <div key={img.id} className="relative rounded overflow-hidden border border-border bg-background">
                    <img src={img.url} alt="" className="w-full aspect-square object-cover" />
                    <ImageExperiencesCell imageTable={ONSITE_GALLERY_IMAGE_TABLE} imageId={img.id} />
                    <div className="flex items-center justify-between gap-1 bg-black/60 p-1">
                      <div className="flex">
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-white hover:text-white" disabled={i === 0} onClick={() => moveGallery(i, -1)}><ArrowUp className="w-3 h-3" /></Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-white hover:text-white" disabled={i === gallery.length - 1} onClick={() => moveGallery(i, 1)}><ArrowDown className="w-3 h-3" /></Button>
                      </div>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-white hover:text-white" onClick={() => removeGallery(i)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* =============================================================
 * NEARBY
 * ============================================================= */

const NEARBY_PAGE_KEY = "nearby";

async function uploadNearby(folder: string, file: File): Promise<string> {
  const { url } = await uploadOptimizedImage({
    file,
    bucket: "slide-media",
    folder: `nearby/${folder}`,
  });
  return url;
}
const uploadNearbyHero = (f: File) => uploadNearby("hero", f);

function NearbyAdminPanel() {
  const fetch = useServerFn(getNearbyPageData);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "nearby"],
    queryFn: () => fetch(),
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "nearby"] });
    qc.invalidateQueries({ queryKey: ["nearby", "page"] });
  };
  if (isLoading || !data) {
    return <div className="text-sm text-muted-foreground py-6">Loading…</div>;
  }
  return (
    <div className="space-y-10">
      <NearbyContentPanel content={data.content} onSaved={invalidate} />
      <AttractionsPanel attractions={data.attractions} onChanged={invalidate} />
    </div>
  );
}

function NearbyContentPanel({
  content,
  onSaved,
}: {
  content: NearbyContent | null;
  onSaved: () => void;
}) {
  const upsert = useServerFn(upsertNearbyContent);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [heroUrl, setHeroUrl] = useState(content?.hero_image_url ?? "");
  const [kicker, setKicker] = useState(content?.hero_kicker ?? "");
  const [title, setTitle] = useState(content?.hero_title ?? "");
  const [subtitle, setSubtitle] = useState(content?.hero_subtitle ?? "");
  const [intro, setIntro] = useState(content?.intro_body ?? "");
  const [tabLabel, setTabLabel] = useState(content?.tab_label ?? "");

  const save = useMutation({
    mutationFn: () =>
      upsert({
        data: {
          id: content?.id,
          hero_image_url: heroUrl || null,
          hero_kicker: kicker || null,
          hero_title: title || null,
          hero_subtitle: subtitle || null,
          intro_body: intro || null,
          tab_label: tabLabel || null,
        },
      }),
    onSuccess: () => { toast.success("Saved"); onSaved(); },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const handleUpload = async (f: File) => {
    setUploading(true);
    try {
      const url = await uploadNearby("hero", f);
      setHeroUrl(url);
      await upsert({
        data: {
          id: content?.id,
          hero_image_url: url,
          hero_kicker: kicker || null,
          hero_title: title || null,
          hero_subtitle: subtitle || null,
          intro_body: intro || null,
          tab_label: tabLabel || null,
        },
      });
      toast.success("Hero image saved");
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5 space-y-5">
      <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Hero & intro</h2>
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Hero image</Label>
          <FieldOverridesButton pageKey={NEARBY_PAGE_KEY} fieldKey="hero_image" label="Hero image" type="image" onUploadImage={uploadNearbyHero} />
        </div>
        <div className="flex items-start gap-3">
          {heroUrl ? <img src={heroUrl} alt="" className="w-32 h-32 object-cover rounded" /> : <div className="w-32 h-32 rounded bg-muted" />}
          <div className="flex-1 space-y-2">
            <Input value={heroUrl} onChange={(e) => setHeroUrl(e.target.value)} placeholder="https://… or upload" />
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); }} />
            <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Upload
            </Button>
          </div>
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2"><Label>Kicker</Label><FieldOverridesButton pageKey={NEARBY_PAGE_KEY} fieldKey="hero_kicker" label="Kicker" type="text" /></div>
        <Input value={kicker} onChange={(e) => setKicker(e.target.value)} placeholder="Beyond the Property" />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2"><Label>Title</Label><FieldOverridesButton pageKey={NEARBY_PAGE_KEY} fieldKey="hero_title" label="Title" type="text" /></div>
        <Textarea value={title} onChange={(e) => setTitle(e.target.value)} rows={2} placeholder={"Nearby\n(press Enter to break line)"} />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2"><Label>Tab button label</Label><FieldOverridesButton pageKey={NEARBY_PAGE_KEY} fieldKey="tab_label" label="Tab button label" type="text" /></div>
        <Input value={tabLabel} onChange={(e) => setTabLabel(e.target.value)} placeholder="Defaults to the hero title" />
        <p className="text-xs text-muted-foreground mt-1">Text shown on the tab switcher button. Leave blank to use the hero title.</p>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2"><Label>Subtitle</Label><FieldOverridesButton pageKey={NEARBY_PAGE_KEY} fieldKey="hero_subtitle" label="Subtitle" type="longtext" /></div>
        <Textarea value={subtitle} onChange={(e) => setSubtitle(e.target.value)} rows={2} />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2"><Label>Intro paragraph</Label><FieldOverridesButton pageKey={NEARBY_PAGE_KEY} fieldKey="intro_body" label="Intro paragraph" type="longtext" /></div>
        <Textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={4} placeholder="Line breaks are preserved." />
      </div>
      <Button onClick={() => save.mutate()} disabled={save.isPending}>
        {save.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save
      </Button>
    </div>
  );
}

function AttractionsPanel({ attractions, onChanged }: { attractions: NearbyAttraction[]; onChanged: () => void }) {
  const reorder = useServerFn(reorderNearbyAttractions);
  const del = useServerFn(deleteNearbyAttraction);
  const [editing, setEditing] = useState<NearbyAttraction | null>(null);
  const [creating, setCreating] = useState(false);

  const move = async (idx: number, dir: -1 | 1) => {
    const next = [...attractions];
    const t = idx + dir;
    if (t < 0 || t >= next.length) return;
    [next[idx], next[t]] = [next[t], next[idx]];
    await reorder({ data: { ordered_ids: next.map((a) => a.id) } });
    onChanged();
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Attractions</h2>
        <Button size="sm" variant="outline" onClick={() => setCreating(true)}><Plus className="w-4 h-4 mr-1" /> Add attraction</Button>
      </div>
      {attractions.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">No attractions yet.</p>
      ) : (
        <div className="space-y-2">
          {attractions.map((at, i) => (
            <div key={at.id} className="flex items-center gap-3 rounded-md border border-border bg-background p-2">
              {at.cover_image_url ? <img src={at.cover_image_url} alt="" className="w-16 h-16 object-cover rounded" /> : <div className="w-16 h-16 rounded bg-muted" />}
              <div className="flex-1 min-w-0">
                <div className="font-serif truncate">{at.label}</div>
                <div className="text-xs text-muted-foreground truncate">{at.slug} · {at.gallery.length} image{at.gallery.length === 1 ? "" : "s"}</div>
              </div>
              <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp className="w-3.5 h-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === attractions.length - 1} onClick={() => move(i, 1)}><ArrowDown className="w-3.5 h-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(at)}><Pencil className="w-3.5 h-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={async () => { if (!confirm(`Delete "${at.label}"?`)) return; await del({ data: { id: at.id } }); onChanged(); }}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
        </div>
      )}
      {editing && <AttractionEditor attraction={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />}
      {creating && <AttractionEditor attraction={null} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); onChanged(); }} />}
    </div>
  );
}

function AttractionEditor({ attraction, onClose, onSaved }: { attraction: NearbyAttraction | null; onClose: () => void; onSaved: () => void }) {
  const upsert = useServerFn(upsertNearbyAttraction);
  const [slug, setSlug] = useState(attraction?.slug ?? "");
  const [label, setLabel] = useState(attraction?.label ?? "");
  const [cover, setCover] = useState(attraction?.cover_image_url ?? "");
  const [description, setDescription] = useState(attraction?.description ?? "");
  const [gallery, setGallery] = useState<NearbyGalleryImage[]>(attraction?.gallery ?? []);
  const [coverUploading, setCoverUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const coverRef = useRef<HTMLInputElement | null>(null);
  const galleryRef = useRef<HTMLInputElement | null>(null);
  const slugBase = attraction?.slug || slug || "new";

  const handleCover = async (f: File) => {
    setCoverUploading(true);
    try { const url = await uploadNearby(`${slugBase}/cover`, f); setCover(url); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Upload failed"); }
    finally { setCoverUploading(false); if (coverRef.current) coverRef.current.value = ""; }
  };
  const handleGallery = async (files: FileList) => {
    setGalleryUploading(true);
    try {
      const uploaded: NearbyGalleryImage[] = [];
      for (const f of Array.from(files)) {
        const url = await uploadNearby(`${slugBase}/gallery`, f);
        uploaded.push({ id: crypto.randomUUID(), url });
      }
      setGallery((g) => [...g, ...uploaded]);
    } catch (e) { toast.error(e instanceof Error ? e.message : "Upload failed"); }
    finally { setGalleryUploading(false); if (galleryRef.current) galleryRef.current.value = ""; }
  };
  const moveGallery = (idx: number, dir: -1 | 1) => {
    const t = idx + dir; if (t < 0 || t >= gallery.length) return;
    const next = [...gallery]; [next[idx], next[t]] = [next[t], next[idx]]; setGallery(next);
  };
  const removeGallery = (idx: number) => setGallery((g) => g.filter((_, i) => i !== idx));
  const autoSlug = (val: string) => val.toLowerCase().replace(/['']/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);

  const save = async () => {
    if (!label.trim()) { toast.error("Label is required"); return; }
    const finalSlug = slug.trim() || autoSlug(label);
    if (!/^[a-z0-9-]+$/.test(finalSlug)) { toast.error("Slug must be lowercase letters, numbers, and dashes"); return; }
    setSaving(true);
    try {
      await upsert({ data: { id: attraction?.id, slug: finalSlug, label: label.trim(), cover_image_url: cover || null, description: description || null, gallery } });
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Save failed"); }
    finally { setSaving(false); }
  };

  const isNew = !attraction;
  const fieldPrefix = `attraction:${attraction?.slug ?? ""}`;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90svh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isNew ? "New attraction" : `Edit: ${attraction!.label}`}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Label</Label>
              {!isNew && <FieldOverridesButton pageKey={NEARBY_PAGE_KEY} fieldKey={`${fieldPrefix}:label`} label={`${attraction!.label} — Label`} type="text" />}
            </div>
            <Input value={label} onChange={(e) => { setLabel(e.target.value); if (isNew && !slug) setSlug(autoSlug(e.target.value)); }} placeholder="Sedona" />
          </div>
          <div>
            <Label className="mb-2 block">Slug</Label>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="sedona" />
            <p className="text-xs text-muted-foreground mt-1">Used internally. Lowercase letters, numbers, and dashes.</p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Cover photo</Label>
              {!isNew && <FieldOverridesButton pageKey={NEARBY_PAGE_KEY} fieldKey={`${fieldPrefix}:cover`} label={`${attraction!.label} — Cover`} type="image" onUploadImage={(f) => uploadNearby(`${attraction!.slug}/cover`, f)} />}
            </div>
            <div className="flex items-start gap-3">
              {cover ? <img src={cover} alt="" className="w-24 h-24 object-cover rounded" /> : <div className="w-24 h-24 rounded bg-muted" />}
              <div className="flex-1 space-y-2">
                <Input value={cover} onChange={(e) => setCover(e.target.value)} placeholder="https://… or upload" />
                <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCover(f); }} />
                <Button type="button" size="sm" variant="outline" onClick={() => coverRef.current?.click()} disabled={coverUploading}>
                  {coverUploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Upload
                </Button>
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Description</Label>
              {!isNew && <FieldOverridesButton pageKey={NEARBY_PAGE_KEY} fieldKey={`${fieldPrefix}:description`} label={`${attraction!.label} — Description`} type="longtext" />}
            </div>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder="Shown in the popup." />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Gallery images</Label>
              <div>
                <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { if (e.target.files?.length) handleGallery(e.target.files); }} />
                <Button size="sm" variant="outline" onClick={() => galleryRef.current?.click()} disabled={galleryUploading}>
                  {galleryUploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Add images
                </Button>
              </div>
            </div>
            {gallery.length === 0 ? <p className="text-sm text-muted-foreground italic">No gallery images yet.</p> : (
              <div className="grid grid-cols-3 gap-2">
                {gallery.map((img, i) => (
                  <div key={img.id} className="relative rounded overflow-hidden border border-border bg-background">
                    <img src={img.url} alt="" className="w-full aspect-square object-cover" />
                    <ImageExperiencesCell imageTable={NEARBY_GALLERY_IMAGE_TABLE} imageId={img.id} />
                    <div className="flex items-center justify-between gap-1 bg-black/60 p-1">
                      <div className="flex">
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-white hover:text-white" disabled={i === 0} onClick={() => moveGallery(i, -1)}><ArrowUp className="w-3 h-3" /></Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-white hover:text-white" disabled={i === gallery.length - 1} onClick={() => moveGallery(i, 1)}><ArrowDown className="w-3 h-3" /></Button>
                      </div>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-white hover:text-white" onClick={() => removeGallery(i)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
